import { Box, styled } from "@mui/material";
import { createPortal } from "react-dom";

import { useDispatchWindows, useDockedWindows, windowsActions } from "contexts/windows";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";

import { DOCK_EDGE_MARGIN, DOCK_LEFT, DOCK_PANEL_GAP, DOCK_PANEL_WIDTH, DOCK_Z_INDEX } from "./constants";
import { DockedWindow } from "./DockedWindow";
import { useDockLayout } from "./useDockLayout";

const Container = styled(Box)`
    position: absolute;
    left: ${DOCK_LEFT}px;
    top: ${DOCK_EDGE_MARGIN}px;
    bottom: ${DOCK_EDGE_MARGIN}px;
    width: ${DOCK_PANEL_WIDTH}px;
    display: flex;
    flex-direction: column;
    gap: ${DOCK_PANEL_GAP}px;
    z-index: ${DOCK_Z_INDEX};
    pointer-events: none;
`;

/**
 * Left-edge dock for windows with `docked: true`. Portaled into the render
 * container so it shares the floating-window coordinate system; the container
 * disables pointer events so the canvas stays interactive between panels.
 */
export function DockManager() {
    const dockedWindows = useDockedWindows();
    const dispatch = useDispatchWindows();
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const { setZoneRef, slots } = useDockLayout(dockedWindows);

    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (!container) {
        return null;
    }
    if (slots.length === 0) {
        return null;
    }

    return createPortal(
        <Container
            ref={setZoneRef}
            data-testid="dock-manager"
            style={{ visibility: nonMarkerUIIsHidden ? "hidden" : undefined }}
        >
            {slots.map((slot, index) => (
                <DockedWindow
                    key={slot.id}
                    entry={slot.entry}
                    height={slot.height}
                    isLast={index === slots.length - 1}
                    onClose={() => dispatch(windowsActions.closeWindow(slot.id))}
                    onToggleMinimize={() => dispatch(windowsActions.toggleMinimizedWindow(slot.id))}
                    onSetHeight={(h) => dispatch(windowsActions.setDockedHeight(slot.id, h))}
                />
            ))}
        </Container>,
        container,
    );
}
